import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Download, Wallet, AlertTriangle, Hash, Activity, Filter } from "lucide-react";

/**
 * iter58 — Internal LLM Usage Accounting dashboard.
 *
 * Admin-only. Reads `/api/admin/llm-cost/*`. Renders four areas:
 *   - Totals: today, this month, retries/regenerates, failed.
 *   - Breakdowns: by project · by user · by stage · by model · by run_type.
 *   - Filtered events table (no raw prompt body anywhere).
 *   - CSV export download.
 *
 * Honesty:
 *   - All cost numbers are best-estimate. We show the cost_source on every
 *     row so admins can audit estimation vs provider-reported.
 *   - We never show prompt text — only prompt_chars + prompt_hash.
 */
export default function LlmCostPanel() {
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [events, setEvents] = useState([]);
    const [filters, setFilters] = useState({
        project_id: "",
        stage_key: "",
        run_type: "",
        model: "",
        success: "",
        limit: 100,
    });

    const reloadSummary = async () => {
        try {
            const { data } = await api.get("/admin/llm-cost/summary");
            setSummary(data);
        } catch (e) {
            toast.error("Failed to load cost summary");
        }
    };

    const reloadEvents = async () => {
        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => {
                if (v !== "" && v != null) params.set(k, v);
            });
            const { data } = await api.get(`/admin/llm-cost/events?${params.toString()}`);
            setEvents(data.items || []);
        } catch (e) {
            toast.error("Failed to load events");
        }
    };

    useEffect(() => {
        setLoading(true);
        Promise.all([reloadSummary(), reloadEvents()]).finally(() => setLoading(false));
    }, []);

    const downloadCsv = () => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
            if (v !== "" && v != null) params.set(k, v);
        });
        // Build the absolute URL so the browser triggers the download dialog.
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        const token = localStorage.getItem("aivos_token") || "";
        const url = `${base}/api/admin/llm-cost/export.csv?${params.toString()}`;
        // Best-effort: hit the URL with the token in the query for download
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then(async (r) => {
                const blob = await r.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = "furmais-llm-cost.csv";
                a.click();
            })
            .catch(() => toast.error("CSV download failed"));
    };

    if (loading || !summary) {
        return (
            <div className="text-sm text-zinc-500 px-2 py-8" data-testid="llm-cost-loading">
                Loading cost data…
            </div>
        );
    }

    const t = summary.totals || {};
    const todayUsd = (t.today && t.today.total_usd) || 0;
    const todayInr = (t.today && t.today.total_inr) || 0;
    const monthUsd = (t.this_month && t.this_month.total_usd) || 0;
    const monthInr = (t.this_month && t.this_month.total_inr) || 0;
    const retries = (t.retries_or_regenerates && t.retries_or_regenerates.events) || 0;
    const failed = (t.failed_calls && t.failed_calls.events) || 0;

    return (
        <div className="space-y-6" data-testid="llm-cost-panel">
            {/* ─── Totals strip ─── */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <StatTile
                    label="Today"
                    valueUsd={todayUsd}
                    valueInr={todayInr}
                    events={(t.today && t.today.events) || 0}
                    icon={Activity}
                    testId="llm-cost-today"
                />
                <StatTile
                    label="This Month"
                    valueUsd={monthUsd}
                    valueInr={monthInr}
                    events={(t.this_month && t.this_month.events) || 0}
                    icon={Wallet}
                    testId="llm-cost-month"
                />
                <StatTile
                    label="Retries / Regens"
                    valueUsd={(t.retries_or_regenerates && t.retries_or_regenerates.total_usd) || 0}
                    valueInr={(t.retries_or_regenerates && t.retries_or_regenerates.total_inr) || 0}
                    events={retries}
                    icon={RefreshCw}
                    testId="llm-cost-retries"
                    accent="amber"
                />
                <StatTile
                    label="Failed Calls"
                    valueUsd={(t.failed_calls && t.failed_calls.total_usd) || 0}
                    valueInr={(t.failed_calls && t.failed_calls.total_inr) || 0}
                    events={failed}
                    icon={AlertTriangle}
                    testId="llm-cost-failed"
                    accent="rose"
                />
            </div>

            {/* ─── Breakdown grid ─── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Breakdown title="By Project" rows={summary.by_project} testId="llm-cost-by-project" />
                <Breakdown title="By User" rows={summary.by_user} testId="llm-cost-by-user" />
                <Breakdown title="By Stage" rows={summary.by_stage} testId="llm-cost-by-stage" />
                <Breakdown title="By Model" rows={summary.by_model} testId="llm-cost-by-model" />
            </div>

            {/* ─── Filters + Events table ─── */}
            <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 overflow-hidden">
                <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 text-zinc-300 text-sm font-medium">
                        <Filter className="size-4 text-zinc-500" />
                        Events
                        <span className="text-zinc-600 text-xs">({events.length})</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="llm-cost-refresh-btn"
                            onClick={() => { reloadSummary(); reloadEvents(); }}
                            className="rounded-md border border-zinc-800 px-2.5 py-1.5 text-xs text-zinc-300 hover:bg-zinc-900 inline-flex items-center gap-1.5"
                        >
                            <RefreshCw className="size-3.5" /> Refresh
                        </button>
                        <button
                            data-testid="llm-cost-csv-btn"
                            onClick={downloadCsv}
                            className="rounded-md border border-emerald-700/60 bg-emerald-600/15 px-2.5 py-1.5 text-xs text-emerald-200 hover:bg-emerald-600/25 inline-flex items-center gap-1.5"
                        >
                            <Download className="size-3.5" /> CSV
                        </button>
                    </div>
                </div>
                <div className="px-4 py-3 border-b border-zinc-800 grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
                    <FilterInput label="Project ID" value={filters.project_id} onChange={(v) => setFilters({ ...filters, project_id: v })} testId="llm-cost-filter-project" />
                    <FilterInput label="Stage" value={filters.stage_key} onChange={(v) => setFilters({ ...filters, stage_key: v })} testId="llm-cost-filter-stage" />
                    <FilterInput label="Run Type" value={filters.run_type} onChange={(v) => setFilters({ ...filters, run_type: v })} testId="llm-cost-filter-runtype" />
                    <FilterInput label="Model" value={filters.model} onChange={(v) => setFilters({ ...filters, model: v })} testId="llm-cost-filter-model" />
                    <select
                        data-testid="llm-cost-filter-success"
                        value={filters.success}
                        onChange={(e) => setFilters({ ...filters, success: e.target.value })}
                        className="rounded-md bg-zinc-900 border border-zinc-800 text-zinc-200 px-2 py-1.5"
                    >
                        <option value="">All</option>
                        <option value="true">Success</option>
                        <option value="false">Failed</option>
                    </select>
                    <button
                        data-testid="llm-cost-apply-filters"
                        onClick={reloadEvents}
                        className="rounded-md bg-zinc-100 text-zinc-900 px-2 py-1.5 font-medium hover:bg-white"
                    >
                        Apply
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-zinc-900 text-zinc-400">
                            <tr>
                                <Th>Time</Th>
                                <Th>Run Type</Th>
                                <Th>Stage</Th>
                                <Th>Project</Th>
                                <Th>Model</Th>
                                <Th>Tokens</Th>
                                <Th>USD</Th>
                                <Th>INR</Th>
                                <Th>Latency</Th>
                                <Th>Status</Th>
                                <Th>Hash</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.length === 0 ? (
                                <tr><td colSpan={11} className="text-zinc-600 text-center py-6">No events yet.</td></tr>
                            ) : events.map((ev) => (
                                <tr key={ev.event_id} className="border-t border-zinc-900 hover:bg-zinc-900/40">
                                    <Td>{(ev.created_at || "").slice(0, 19).replace("T", " ")}</Td>
                                    <Td><Pill>{ev.run_type || "other"}</Pill></Td>
                                    <Td>{ev.stage_key || <span className="text-zinc-600">—</span>}</Td>
                                    <Td>{ev.project_id ? ev.project_id.slice(0, 8) : <span className="text-zinc-600">—</span>}</Td>
                                    <Td className="font-mono">{ev.model}</Td>
                                    <Td>{ev.total_tokens}</Td>
                                    <Td className="text-emerald-400">${(ev.estimated_usd_cost || 0).toFixed(4)}</Td>
                                    <Td className="text-zinc-400">₹{(ev.estimated_inr_cost || 0).toFixed(2)}</Td>
                                    <Td className="text-zinc-500">{ev.latency_ms}ms</Td>
                                    <Td>
                                        {ev.success ? (
                                            <span className="text-emerald-400">ok</span>
                                        ) : (
                                            <span className="text-rose-400" title={ev.error_message_sanitized || ev.error_type}>fail</span>
                                        )}
                                    </Td>
                                    <Td>
                                        {ev.prompt_hash ? (
                                            <span className="inline-flex items-center gap-1 text-zinc-500 font-mono">
                                                <Hash className="size-3" />{ev.prompt_hash.slice(0, 6)}
                                            </span>
                                        ) : <span className="text-zinc-700">—</span>}
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="text-[11px] text-zinc-600 px-1">
                Costs are estimates derived from token counts × Anthropic / OpenAI / Google published list prices.
                Provider-reported costs override estimates when available. We never store raw prompt text — only
                prompt_chars + 16-char prompt_hash. Conversion rate: ₹{summary.usd_inr_rate}/USD.
            </p>
        </div>
    );
}

function StatTile({ label, valueUsd, valueInr, events, icon: Icon, testId, accent }) {
    const accentBorder = accent === "amber" ? "border-amber-700/40 bg-amber-950/20" :
        accent === "rose" ? "border-rose-700/40 bg-rose-950/20" :
            "border-zinc-800 bg-zinc-950/50";
    return (
        <div data-testid={testId} className={`rounded-xl border ${accentBorder} px-4 py-3`}>
            <div className="flex items-center justify-between text-zinc-400 text-xs">
                <span>{label}</span>
                {Icon ? <Icon className="size-4" /> : null}
            </div>
            <div className="mt-1 text-zinc-100 font-semibold text-lg">${valueUsd.toFixed(4)}</div>
            <div className="text-zinc-500 text-xs">₹{valueInr.toFixed(2)} · {events} events</div>
        </div>
    );
}

function Breakdown({ title, rows, testId }) {
    return (
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 overflow-hidden" data-testid={testId}>
            <div className="px-4 py-2 border-b border-zinc-800 text-zinc-300 text-sm font-medium">{title}</div>
            <div className="max-h-64 overflow-y-auto">
                {!rows || rows.length === 0 ? (
                    <div className="text-zinc-600 text-xs px-4 py-4">No data.</div>
                ) : (
                    <table className="w-full text-xs">
                        <tbody>
                            {rows.slice(0, 12).map((r) => (
                                <tr key={String(r.key)} className="border-t border-zinc-900">
                                    <td className="px-4 py-1.5 text-zinc-300 truncate max-w-[200px]" title={String(r.key || "—")}>
                                        {r.key ? String(r.key).slice(0, 28) : <span className="text-zinc-600">—</span>}
                                    </td>
                                    <td className="px-3 py-1.5 text-emerald-400 text-right">${(r.total_usd || 0).toFixed(4)}</td>
                                    <td className="px-3 py-1.5 text-zinc-500 text-right">{r.events}×</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function FilterInput({ label, value, onChange, testId }) {
    return (
        <input
            data-testid={testId}
            placeholder={label}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="rounded-md bg-zinc-900 border border-zinc-800 text-zinc-200 placeholder-zinc-600 px-2 py-1.5"
        />
    );
}

function Th({ children }) {
    return <th className="text-left px-3 py-2 font-medium">{children}</th>;
}

function Td({ children, className = "" }) {
    return <td className={`px-3 py-1.5 text-zinc-300 ${className}`}>{children}</td>;
}

function Pill({ children }) {
    return (
        <span className="inline-flex items-center rounded-md bg-zinc-900 px-1.5 py-0.5 text-[10px] text-zinc-400 border border-zinc-800">
            {children}
        </span>
    );
}
